import Image from 'next/image';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import { generateArticleSchema } from '../../utils/schemaGenerators';


export const metadata = {
  title: 'Top 10 Trends in Bathroom Remodeling',
  image: '/blog/bathroom-remodeling-trends.webp',
  date: 'April 5, 2025',
  author: 'Modern Build Team',
  excerpt: 'Explore the top 10 bathroom remodeling trends of 2025—from spa-style upgrades to sustainable design ideas—perfect for homeowners planning their next renovation.',
  category: 'Bathroom Remodeling',
  tags: ['Bathroom Remodeling', 'General Home Improvement', 'Remodeling & Renovation', 'Home Renovation'],
};

export default function Top10BathroomTrends() {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="title" content={metadata.title} />
        <meta name="description" content={metadata.excerpt} />
        <meta name="author" content={metadata.author} />
        <meta name="keywords" content={metadata.tags.join(', ')} />
        <link rel="canonical" href={`https://modernbuildremodel.com/blog/top-10-bathroom-trends`} />

        {/* Open Graph */}
        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.excerpt} />
        <meta property="og:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta property="og:url" content={`https://modernbuildremodel.com/blog/top-10-bathroom-trends`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.excerpt} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": `https://modernbuildremodel.com/blog`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Top 10 Bathroom Trends",
                  "item": `https://modernbuildremodel.com/blog/top-10-bathroom-trends`
                },
              ]
            })
          }}
        />
      
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateArticleSchema(metadata, 'top-10-bathroom-trends')),
          }}
        />

      </Head>

      <Header />
      
      <div className="bg-white">
        <Hero category="top10BathroomTrends" page="page" hasContentH1={false}/>

        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <p className="text-sm text-black mb-2">
           <span className="capitalize text-primary tracking-[3px]">By {metadata.author} / {metadata.date}</span>
          </p>

          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] mb-10 rounded-lg overflow-hidden">
            <Image
              src={metadata.image}
              alt={`Modern Build Home Remodeling post: ${metadata.title}`}
              title={metadata.title}
              fill
              sizes="(max-width: 768px) 100vw, 700px"
              className="object-cover rounded-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none text-md text-black">
            <p>
              Bathroom remodeling is one of the most rewarding home improvement projects, offering both comfort and value. Whether you’re planning a full-scale renovation or a simple refresh, here are 10 trends making waves in 2025:
            </p>

            <ol className="list-decimal mt-5 pl-5 space-y-4">
              <li><strong>Spa-Like Atmospheres:</strong> Think heated floors, rainfall showers, and soaking tubs to create a personal sanctuary.</li>
              <li><strong>Floating Vanities:</strong> These modern fixtures give bathrooms a sleek, open feel while adding practical storage.</li>
              <li><strong>Statement Lighting:</strong> Dramatic pendant lights or wall-mounted fixtures elevate style and function.</li>
              <li><strong>Natural Materials:</strong> Wood accents, stone sinks, and organic textures bring warmth and authenticity.</li>
              <li><strong>Large-Format Tiles:</strong> These reduce grout lines and create a clean, expansive look.</li>
              <li><strong>Smart Technology:</strong> Integrated mirrors, touchless faucets, and smart showers add convenience.</li>
              <li><strong>Black Fixtures:</strong> A bold alternative to chrome, black hardware adds modern contrast and depth.</li>
              <li><strong>Built-In Niches:</strong> Recessed shelving in showers or walls for a clean, functional aesthetic.</li>
              <li><strong>Earthy Color Palettes:</strong> Think olive green, terracotta, and creamy neutrals for a calming vibe.</li>
              <li><strong>Sustainability:</strong> Low-flow fixtures, energy-efficient lighting, and eco-conscious materials are in demand.</li>
            </ol>

            <p>These trends reflect a growing desire for bathrooms that are both high-performing and high-style. Ready to transform your space? Let Modern Build bring your dream bathroom to life.</p>
          </div>
        </div>

        <SocialProof />
        <ContactUs />
        <Footer />
        </div>
    </>
  );
}
